import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Subject, takeUntil } from 'rxjs';
import { validatorErrors } from 'src/app/common/class/data/validator/validator-errors';
import { PasswordInputType } from 'src/app/common/constant/PasswordInputType';
import { Logger } from 'src/app/common/utility/logger/logger';
import { CognitoService } from 'src/app/services/authentication/cognito/cognito.service';
import { PasswordToggle } from '../../common/constant/PasswordToggle';

const controls = {
  oldPassword: 'oldPassword',
  newPassword: 'newPassword',
  repeatPassword: 'repeatPassword',
};

enum ExitEvent {
  Cancel = "cancel",
  Apply = 'apply'
}

@Component({
  selector: 'ems-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent implements OnDestroy {
  private passwordToggles: Array<boolean> = new Array(3).fill(false)
  private unsubscribe: Subject<void> = new Subject();
  private attemptError: string | undefined
  private showPolicy: boolean = false

  public form: FormGroup = this.createForm();

  public get AttemptError(): string | undefined {
    return this.attemptError
  }

  public get oldLabel(): string {
    return this.getLabel('old')
  }

  public get newLabel(): string {
    return this.getLabel('new')
  }

  public get repeatLabel(): string {
    return this.getLabel('repeat')
  }

  public get oldPassword(): FormControl {
    return this.getFormControl(controls.oldPassword)
  }

  public get newPassword(): FormControl {
    return this.getFormControl(controls.newPassword)
  }

  public get repeatPassword(): FormControl {
    return this.getFormControl(controls.repeatPassword)
  }

  public get title(): string {
    return this.getLabel('title.change')
  }

  public get passwordToggle(): typeof PasswordToggle {
    return PasswordToggle
  }

  public get defaultError(): string {
    return this.getError('generic')
  }

  public get applyDisabled(): boolean {
    return this.errors || !this.formFilled
  }

  public get errors(): boolean {
    return !!this.allErrors.length
  }

  public get allErrors(): Array<string> {
    const returnValue: Array<string> = [
      ...this.oldPasswordErrors,
      ...this.newPasswordErrors,
      ...this.repeatPasswordErrors,
    ]
    return returnValue
  }

  public get oldPasswordErrors(): Array<string> {
    return this.getPasswordErrors(this.oldPassword)
  }

  public get newPasswordErrors(): Array<string> {
    const returnValue = this.getPasswordErrors(this.newPassword)
    if (!!this.newPassword.value && this.newPassword.value === this.oldPassword.value) returnValue.push(this.getError('same'))
    return returnValue
  }

  public get repeatPasswordErrors(): Array<string> {
    const returnValue = this.getPasswordErrors(this.repeatPassword)
    if (this.repeatPassword.value !== this.newPassword.value) returnValue.push(this.getError('no-match'))
    return returnValue
  }

  public get ShowPolicy(): boolean {
    return this.showPolicy
  }

  public apply(): void {
    this.cognitoService.changePassword(this.oldPassword.value, this.newPassword.value).pipe(
      takeUntil(this.unsubscribe)
    ).subscribe(result => {
      Logger.debug(`Result: ${JSON.stringify(result)}`)
      this.attemptError = undefined
      result
        .then((message: any) => {
          Logger.debug(`Password change result: ${JSON.stringify(message)}`)
          this.leave(ExitEvent.Apply)
        })
        .catch((error: any) => {
          Logger.debug(`Password change failed: ${JSON.stringify(error)}`)
          this.attemptError = error.message
        })
    })
  }

  public cancel(): void {
    this.leave(ExitEvent.Cancel)
  }

  public togglePasswordVisibility(identifier: PasswordToggle): void {
    this.passwordToggles[identifier] = !this.passwordToggles[identifier]
  }

  public passwordVisible(identifier: PasswordToggle): boolean {
    return this.passwordToggles[identifier]
  }

  public passwordInputType(identifier: PasswordToggle): string {
    return this.passwordVisible(identifier) ? PasswordInputType.ClearText : PasswordInputType.MaskedInput
  }

  public togglePolicyVisibility(): void {
    this.showPolicy = !this.showPolicy
  }

  constructor(
    private formBuilder: FormBuilder,
    private cognitoService: CognitoService,
    private dialogRef: MatDialogRef<ChangePasswordComponent>
  ) { }


  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private getPasswordErrors(passwordControl: FormControl): Array<string> {
    const returnValue: Array<string> = []
    if (passwordControl.pristine) return returnValue
    if (passwordControl.hasError(validatorErrors.required)) returnValue.push(this.getError(validatorErrors.required))
    return returnValue
  }

  private getLabel(key: string): string {
    return `password.${key}`
  }

  private getError(key: string): string {
    return this.getLabel(`errors.${key}`)
  }

  private createForm(): FormGroup {
    return this.formBuilder.group({
      oldPassword: [null, [Validators.required]],
      newPassword: [null, [Validators.required]],
      repeatPassword: [null, [Validators.required]]
    });
  }

  private getFormControl(controlName: string): FormControl {
    return this.form.controls[controlName] as FormControl;
  }

  private leave(event: ExitEvent): void {
    this.dialogRef.close({
      event: event
    });
  }

  private get formFilled(): boolean {
    return this.populated(this.oldPassword) &&
      this.populated(this.newPassword) &&
      this.populated(this.repeatPassword)
  }

  private populated(control: FormControl): boolean {
    return !!control.value?.length
  }

}
